import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Edit, Trash2, Plus, Shield } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Role {
  id: number;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

interface PermissionModule {
  [module: string]: {
    action: string;
    description: string;
  }[];
}

export default function Roles() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPermissionsForm, setShowPermissionsForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<PermissionModule>({});
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
  });

  const [editRole, setEditRole] = useState({
    name: '',
    description: '',
  });

  // Load roles
  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/roles', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar roles');
      }

      const data = await response.json();
      setRoles(data.roles || []);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al cargar datos');
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load available permissions
  const loadAvailablePermissions = async () => {
    try {
      const response = await fetch('/api/roles/permissions', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar permisos');
      }

      const data = await response.json();
      setAvailablePermissions(data.permissions || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  useEffect(() => {
    loadRoles();
    loadAvailablePermissions();
  }, []);

  // Filter roles based on search term
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Create role function
  const handleCreateRole = async () => {
    if (!newRole.name) {
      alert('Por favor ingrese el nombre del rol');
      return;
    }

    try {
      setCreateLoading(true);
      setError(null);

      const response = await fetch('/api/roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newRole),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear rol');
      }

      await loadRoles();

      setNewRole({
        name: '',
        description: '',
      });
      setShowCreateForm(false);

      alert('Rol creado exitosamente');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      alert(error instanceof Error ? error.message : 'Error al crear rol');
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete role function
  const handleDeleteRole = async (roleId: number, roleName: string) => {
    if (!confirm(`¿Estás seguro de que deseas eliminar el rol "${roleName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/roles/${roleId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar rol');
      }

      await loadRoles();
      alert('Rol eliminado exitosamente');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error al eliminar rol');
    }
  };

  // Update role function
  const handleUpdateRole = async () => {
    if (!editingRole) return;

    if (!editRole.name) {
      alert('Por favor ingrese el nombre del rol');
      return;
    }

    try {
      setEditLoading(true);
      setError(null);

      const response = await fetch(`/api/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(editRole),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar rol');
      }

      await loadRoles();
      setShowEditForm(false);
      alert('Rol actualizado exitosamente');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      alert(error instanceof Error ? error.message : 'Error al actualizar rol');
    } finally {
      setEditLoading(false);
    }
  };

  // Assign permissions function
  const handleAssignPermissions = async () => {
    if (!editingRole) return;

    if (selectedPermissions.length === 0) {
      alert('Debe seleccionar al menos un permiso');
      return;
    }

    try {
      setPermissionsLoading(true);
      setError(null);

      const response = await fetch(`/api/roles/${editingRole.id}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ permissions: selectedPermissions }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al asignar permisos');
      }

      await loadRoles();
      setShowPermissionsForm(false);
      alert('Permisos asignados exitosamente');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      alert(error instanceof Error ? error.message : 'Error al asignar permisos');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const openEditForm = (role: Role) => {
    setEditingRole(role);
    setEditRole({
      name: role.name,
      description: role.description || '',
    });
    setShowEditForm(true);
  };

  const openPermissionsForm = (role: Role) => {
    setEditingRole(role);
    const knownActions = Object.values(availablePermissions).flat().map(p => p.action);
    const validPerms = (role.permissions || []).filter(p => knownActions.length === 0 || knownActions.includes(p));
    setSelectedPermissions(validPerms);
    setShowPermissionsForm(true);
  };

  const togglePermission = (permission: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const selectAllInModule = (moduleName: string) => {
    const modulePermissions = availablePermissions[moduleName]?.map(p => p.action) || [];
    const allSelected = modulePermissions.every(p => selectedPermissions.includes(p));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(p => !modulePermissions.includes(p)));
    } else {
      setSelectedPermissions(prev => {
        const newPerms = [...prev];
        modulePermissions.forEach(p => {
          if (!newPerms.includes(p)) {
            newPerms.push(p);
          }
        });
        return newPerms;
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Roles y Permisos</h2>
          <p className="text-muted-foreground">
            Gestiona roles y permisos del sistema.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Cargando roles...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Roles y Permisos</h2>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Roles y Permisos</h2>
        <p className="text-muted-foreground">
          Gestiona roles y permisos del sistema.
        </p>
      </div>

      {/* Create Role Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Crear Nuevo Rol</CardTitle>
            <CardDescription>Ingresa la información del nuevo rol</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Nombre del Rol *</label>
                <Input
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  placeholder="Ej: Supervisor de Producción"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descripción</label>
                <Input
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Descripción opcional del rol"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateRole} disabled={createLoading}>
                {createLoading ? 'Creando...' : 'Crear Rol'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Role Form */}
      {showEditForm && editingRole && (
        <Card>
          <CardHeader>
            <CardTitle>Editar Rol</CardTitle>
            <CardDescription>Modifica la información del rol</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Nombre del Rol *</label>
                <Input
                  value={editRole.name}
                  onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
                  placeholder="Ej: Supervisor de Producción"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descripción</label>
                <Input
                  value={editRole.description}
                  onChange={(e) => setEditRole({ ...editRole, description: e.target.value })}
                  placeholder="Descripción opcional del rol"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateRole} disabled={editLoading}>
                {editLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
              <Button variant="outline" onClick={() => setShowEditForm(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Permissions Form */}
      {showPermissionsForm && editingRole && (
        <Card>
          <CardHeader>
            <CardTitle>Asignar Permisos: {editingRole.name}</CardTitle>
            <CardDescription>Selecciona los permisos que tendrá este rol</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(availablePermissions).map(([moduleName, permissions]) => (
              <div key={moduleName} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold capitalize">{moduleName}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectAllInModule(moduleName)}
                  >
                    {permissions.every(p => selectedPermissions.includes(p.action))
                      ? 'Deseleccionar Todos'
                      : 'Seleccionar Todos'}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {permissions.map(permission => (
                    <label
                      key={permission.action}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.action)}
                        onChange={() => togglePermission(permission.action)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-sm">{permission.description}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleAssignPermissions} disabled={permissionsLoading}>
                {permissionsLoading ? 'Guardando...' : 'Guardar Permisos'}
              </Button>
              <Button variant="outline" onClick={() => setShowPermissionsForm(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Roles List */}
      <Card>
        <CardHeader>
          <CardTitle>Roles del Sistema</CardTitle>
          <CardDescription>
            Lista de todos los roles configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowCreateForm(!showCreateForm)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Rol
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Nombre</th>
                  <th className="text-left p-3 font-medium">Descripción</th>
                  <th className="text-left p-3 font-medium">Permisos</th>
                  <th className="text-left p-3 font-medium">Tipo</th>
                  <th className="text-right p-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRoles.map((role) => (
                  <tr key={role.id} className="hover:bg-muted/50">
                    <td className="p-3 font-medium">{role.name}</td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {role.description || '-'}
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-muted-foreground">
                        {role.permissions.length} permisos
                      </span>
                    </td>
                    <td className="p-3">
                      {role.isSystem && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Sistema
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPermissionsForm(role)}
                          title="Gestionar Permisos"
                        >
                          <Shield className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditForm(role)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {!role.isSystem && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRole(role.id, role.name)}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRoles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No se encontraron roles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
